/**
 * Breadcrumbs — the trail above the page header.
 *
 * Two ways to use it:
 *
 * 1. Auto mode (default): mount once in the layout, let it derive crumbs
 *    from the current route. It splits the pathname on '/' and looks up
 *    a human label for each segment in the i18n catalog under
 *    `breadcrumbs.<segment>`. Numeric segments (`/cases/123/edit`)
 *    are rendered as `#123` and link to the parent.
 *
 * 2. Explicit mode: pass `crumbs={[{ label, href }, ...]}` to override
 *    auto-derivation — useful when a page knows its parent entity's title
 *    (e.g., `Cases / "Smith vs. Jones" / Edit`) and the auto-derived
 *    `#42` label would be useless.
 *
 * A11y: rendered inside `<nav aria-label="Breadcrumb">` with the current
 * crumb marked `aria-current="page"`. Separator chevron flips under RTL.
 */

import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiOutlineHome, HiOutlineChevronRight } from 'react-icons/hi2'
import { useWorkspace } from '../../contexts/WorkspaceContext'

/**
 * Map a path segment to a human label. Tries:
 *   1. Explicit override in the SEGMENT_OVERRIDES map below
 *   2. Workspace-entity plural (cases → getEntity('case').plural)
 *   3. i18n key under `breadcrumbs.<segment>`
 *   4. Title-cased fallback ('document-groups' → 'Document Groups')
 */
const SEGMENT_TO_ENTITY = {
  clients:    'client',
  cases:      'case',
  sessions:   'session',
  documents:  'document',
  messages:   'message',
  connections: 'connection',
}

function titleCase(s) {
  return s.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function deriveCrumbs(pathname, { t, getEntity }) {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return []

  const crumbs = []
  let acc = ''

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    acc += '/' + seg

    let label
    if (/^\d+$/.test(seg)) {
      // Numeric ID — show as #N. Page-level Breadcrumbs override usually
      // replaces this with the real entity title.
      label = `#${seg}`
    } else if (SEGMENT_TO_ENTITY[seg]) {
      label = getEntity(SEGMENT_TO_ENTITY[seg]).plural
    } else {
      const i18nKey = `breadcrumbs.${seg}`
      const translated = t(i18nKey, { defaultValue: '' })
      label = translated || titleCase(seg)
    }

    crumbs.push({ label, href: acc })
  }

  return crumbs
}

export default function Breadcrumbs({
  crumbs: overrideCrumbs,
  homeHref = '/dashboard',
  className = '',
  includeHome = true,
}) {
  const { t } = useTranslation()
  const { getEntity } = useWorkspace()
  const location = useLocation()

  const computed = useMemo(() => {
    if (overrideCrumbs && overrideCrumbs.length > 0) return overrideCrumbs
    return deriveCrumbs(location.pathname, { t, getEntity })
  }, [overrideCrumbs, location.pathname, t, getEntity])

  // Don't render on the bare home route — there's nothing useful to show.
  if (computed.length === 0) return null
  if (computed.length === 1 && (computed[0].href === homeHref || computed[0].href === '/')) return null

  const items = includeHome
    ? [{ label: t('nav.dashboard'), href: homeHref, isHome: true }, ...computed]
    : computed

  return (
    <nav aria-label={t('common.breadcrumb', 'Breadcrumb')} className={`mb-3 ${className}`}>
      <ol className="flex items-center flex-wrap gap-1 text-xs text-ink-muted">
        {items.map((crumb, idx) => {
          const isLast = idx === items.length - 1
          return (
            <li key={crumb.href + idx} className="inline-flex items-center min-w-0">
              {idx > 0 && (
                <HiOutlineChevronRight
                  className="w-3.5 h-3.5 mx-1 text-ink-disabled flex-shrink-0 rtl:rotate-180"
                  aria-hidden="true"
                />
              )}
              {isLast ? (
                <span
                  aria-current="page"
                  className="font-medium text-ink-primary truncate max-w-[18ch] sm:max-w-[28ch]"
                  title={crumb.label}
                >
                  {crumb.isHome ? <HiOutlineHome className="w-3.5 h-3.5" aria-hidden="true" /> : crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.href}
                  className="hover:text-ink-primary hover:underline transition-colors truncate max-w-[14ch] sm:max-w-[24ch] inline-flex items-center gap-1"
                  title={crumb.label}
                >
                  {crumb.isHome
                    ? <HiOutlineHome className="w-3.5 h-3.5" aria-hidden="true" />
                    : crumb.label}
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
